/**
 * Route-level prefetch helpers.
 *
 * Called from TanStack Router `beforeLoad` to fire off React Query
 * prefetches in parallel with auth resolution. By the time AuthGuard
 * resolves and page components mount, the data is already in cache.
 *
 * Only runs client-side — server-side requests lack auth cookies and
 * would fail, blocking SSR streaming while waiting for the 401.
 *
 * `prefetchQuery` never throws — failed prefetches are silently ignored
 * and the component's own `useQuery` will retry as normal.
 */
import type { QueryClient } from '@tanstack/react-query'
import { api, schoolApi } from './api'
import { queries } from './queries'
import { STALE_TIMES } from './query-config'

const isServer = typeof window === 'undefined'

export function prefetchDevices(queryClient: QueryClient) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.devices.list(),
    queryFn: () => api.getDevices(),
    staleTime: STALE_TIMES.lists,
  })
}

export function prefetchPolicies(queryClient: QueryClient) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.policies.list(),
    queryFn: () => api.getPolicies(),
    staleTime: STALE_TIMES.lists,
  })
}

export function prefetchSchedules(queryClient: QueryClient) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.schedules.list(),
    queryFn: () => api.getSchedules(),
    staleTime: STALE_TIMES.lists,
  })
}

/* ---------- School-scoped prefetches ---------- */

export function prefetchSchoolPolicies(queryClient: QueryClient, schoolId: string) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.school.policies(schoolId),
    queryFn: () => schoolApi.getPolicies(schoolId),
    staleTime: STALE_TIMES.lists,
  })
}

export function prefetchSchoolSchedules(queryClient: QueryClient, schoolId: string) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.school.schedules(schoolId),
    queryFn: () => schoolApi.getSchedules(schoolId),
    staleTime: STALE_TIMES.lists,
  })
}

export function prefetchSchoolGroups(queryClient: QueryClient, schoolId: string) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.school.groups(schoolId),
    queryFn: () => schoolApi.getGroups(schoolId),
    staleTime: STALE_TIMES.lists,
  })
}

export function prefetchSchoolPolicy(queryClient: QueryClient, schoolId: string, policyId: string) {
  if (isServer) return
  queryClient.prefetchQuery({
    queryKey: queries.school.policy(schoolId, policyId),
    queryFn: () => schoolApi.getPolicy(schoolId, policyId),
    staleTime: STALE_TIMES.details,
  })
}
